import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/Icon';
import { Field, FieldDescription, FieldError, FieldLabel } from '@/components/ui/field';

export interface FormFieldProps {
  id: string;
  label: string;
  /** Marked with * before the user ever submits (validation in the field, not in a toast). */
  required?: boolean;
  hint?: string;
  error?: string;
  trailing?: ReactNode;
  children: ReactNode;
  className?: string;
}

/** The Field pattern (shadcn Field + FieldLabel + FieldDescription/FieldError) with Hangar's label row and sizes. */
export function FormField({ id, label, required, hint, error, trailing, children, className }: FormFieldProps) {
  return (
    <Field data-invalid={error ? true : undefined} className={cn('min-w-0 gap-1.5', className)}>
      <div className="flex items-baseline justify-between gap-3">
        <FieldLabel htmlFor={id} className="gap-0.5 text-[13px]">
          {label}
          {required ? (
            <span className="text-primary" aria-hidden>
              *
            </span>
          ) : null}
        </FieldLabel>
        {trailing}
      </div>
      {children}
      {error ? (
        <FieldError id={`${id}-error`} className="flex items-start gap-1 text-xs">
          <Icon name="error" size={14} className="mt-px" />
          <span>{error}</span>
        </FieldError>
      ) : hint ? (
        <FieldDescription id={`${id}-hint`} className="text-xs leading-normal text-subtle">
          {hint}
        </FieldDescription>
      ) : null}
    </Field>
  );
}

/** Wires an input to its FormField messages. */
export function fieldA11y(id: string, error?: string, hint?: string) {
  return {
    id,
    'aria-invalid': error ? true : undefined,
    'aria-describedby': error ? `${id}-error` : hint ? `${id}-hint` : undefined,
  } as const;
}
